import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { heroImage } from '../mock';
import { LogIn, PhoneCall, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AREA_KEYS = [
  { key: 'auslaenderrecht', slug: 'auslaenderrecht' },
  { key: 'steuerberatung', slug: 'steuerberatung' },
  { key: 'steuerrecht', slug: 'steuerrecht' },
  { key: 'gesellschaftsrecht', slug: 'gesellschaftsrecht' },
  { key: 'strafrecht', slug: 'strafrecht' },
  { key: 'verwaltungsrecht', slug: 'verwaltungsrecht' },
  { key: 'mietrecht', slug: 'mietrecht' },
  { key: 'verkehrsrecht', slug: 'verkehrsrecht' },
];

const Hero = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const portalTo = ['admin', 'kanzlei_mitarbeiter'].includes(user?.role) ? '/kanzlei/eingang' : '/dashboard';
  // Headline split into 3 lines: split on " & " then again on " "
  const headline = t('hero.headline');
  const parts = headline.split(/\s*[&]\s*/);
  const lines =
    parts.length === 2
      ? [parts[0].trim(), '&', parts[1].trim()]
      : [headline];

  return (
    <section id="start" className="relative pt-[140px] lg:pt-[150px]">
      <div className="relative w-full h-[calc(100vh-150px)] min-h-[600px] overflow-hidden">
        <img
          src={heroImage}
          alt="Bonn Skyline"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5C]/85 via-[#1B3A5C]/40 to-transparent rtl:bg-gradient-to-l"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex items-center">
          <div className="max-w-2xl text-white">
            <h1 className="font-serif font-light tracking-tight leading-[1.05]" data-testid="hero-headline">
              {lines.map((l, i) => (
                <span key={l} className="block text-4xl md:text-5xl lg:text-6xl mt-2 first:mt-0 uppercase">
                  {l}
                </span>
              ))}
            </h1>

            <p className="mt-10 text-base text-white/85 tracking-wide">{t('hero.subline')}</p>

            <ul className="mt-4 space-y-1">
              {AREA_KEYS.map(({ key, slug }) => (
                <li key={key}>
                  <Link
                    to={`/rechtsgebiete/${slug}`}
                    data-testid={`hero-area-${slug}`}
                    className="inline-block text-sm md:text-base tracking-[0.18em] text-white font-medium hover:text-[#e0b56a] transition-colors uppercase"
                  >
                    {t(`hero.areas.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-cta">
              <Link
                to={isAuthenticated ? portalTo : '/login'}
                data-testid="hero-login-button"
                className="group inline-flex items-center gap-2 bg-[#b8893d] hover:bg-[#a07730] text-white text-xs tracking-[0.22em] uppercase px-6 py-3.5 rounded-sm transition-colors"
              >
                <LogIn className="w-4 h-4" />
                {isAuthenticated ? t('hero.cta_portal') : t('hero.cta_login')}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </Link>
              <Link
                to="/rueckruf"
                data-testid="hero-rueckruf-button"
                className="inline-flex items-center gap-2 border border-white/60 hover:border-white hover:bg-white/10 text-white text-xs tracking-[0.22em] uppercase px-6 py-3.5 rounded-sm transition-colors"
              >
                <PhoneCall className="w-4 h-4" />
                {t('hero.cta_rueckruf')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
